import { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  RadioGroup,
  Radio,
  Stack,
  Button,
} from "@chakra-ui/react";
import { showErrorToast } from "../../../util/errorHandler";

/**
 * Shared create/edit dialog for a saved filter. In edit mode (`initialValues`
 * supplied) the filter configuration itself is not editable here — only
 * name/description/visibility. Re-saving the current filter state under an
 * existing entry is a separate "Save current filter" action, not an edit.
 */
const SaveFilterModal = ({
  isOpen,
  onClose,
  onSave,
  toast,
  initialValues = null,
  title = "Save current filter",
  submitLabel = "Save",
}) => {
  const [name, setName] = useState(initialValues?.name || "");
  const [description, setDescription] = useState(
    initialValues?.description || "",
  );
  const [visibility, setVisibility] = useState(
    initialValues?.visibility || "private",
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialValues?.name || "");
      setDescription(initialValues?.description || "");
      setVisibility(initialValues?.visibility || "private");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showErrorToast(toast, "Name is required");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        name: trimmedName,
        description: description.trim(),
        visibility,
      });
      onClose();
    } catch (error) {
      // onSave callers already surface their own error toast; this only
      // guards against an unhandled rejection closing nothing.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Books from 1822"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Visibility</FormLabel>
              <RadioGroup value={visibility} onChange={setVisibility}>
                <Stack direction="row" spacing={5}>
                  <Radio value="private">Private</Radio>
                  <Radio value="public">Public</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
            {submitLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SaveFilterModal;
